import React, { useState } from "react";
import {
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Grid,
  Box,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import {
  PlayArrow,
  Visibility,
  Memory,
  Extension,
  Speed,
} from "@mui/icons-material";
import RunAndTest from "components/play-ground/Run";

import {
  useGetPendingPublicLevelsQuery,
  useApprovePendingLevelMutation,
  useRejectPendingLevelMutation,
} from "lib/store/service/public";

function PendingLevels() {
  const { data, isLoading, refetch } = useGetPendingPublicLevelsQuery();
  const [approveLevel, { isLoading: isApproving }] = useApprovePendingLevelMutation();
  const [rejectLevel, { isLoading: isRejecting }] = useRejectPendingLevelMutation();

  const [selectedLevel, setSelectedLevel] = useState(null);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const handleOpenPlayModal = (item) => {
    setSelectedLevel(item);
    setPlayModalOpen(true);
  };

  const handleOpenPreview = (item) => {
    setSelectedLevel(item);
    setPreviewModalOpen(true);
  };

  const handleApprove = async (levelId) => {
    try {
      const res = await approveLevel(levelId).unwrap();
      toast.success(res?.message || "✅ Level approved successfully!");
      setPlayModalOpen(false);
      setPreviewModalOpen(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to approve level");
    }
  };

  const handleOpenRejectModal = (item) => {
    setSelectedLevel(item);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please enter a valid rejection reason");
      return;
    }

    try {
      const res = await rejectLevel({ levelId: selectedLevel._id, reason: rejectionReason.trim() }).unwrap();
      toast.success(res?.message || "🔴 Level rejected successfully!");
      setRejectModalOpen(false);
      setPlayModalOpen(false);
      setPreviewModalOpen(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reject level");
    }
  };

  const pendingList = data?.data?.levels || [
    {
      _id: "demo_1",
      levelCode: "#CS-8924",
      title: "Triple XOR Matrix",
      creator: { name: "Alex_Logic", email: "alex@circuitscramble.com", avatar: "https://api.dicebear.com/7.x/bottts/svg?seed=Alex" },
      difficulty: "Medium",
      nodesCount: 7,
      edgesCount: 9,
      bestMoves: 4,
      gateTypes: ["XOR", "AND", "NOT"],
      createdAt: "2026-08-03 14:15",
    },
    {
      _id: "demo_2",
      levelCode: "#CS-4192",
      title: "Quantum NAND Cascade",
      creator: { name: "CircuitWizard", email: "wizard@circuitscramble.com", avatar: "https://api.dicebear.com/7.x/bottts/svg?seed=Wizard" },
      difficulty: "Hard",
      nodesCount: 11,
      edgesCount: 14,
      bestMoves: 8,
      gateTypes: ["NAND", "NOR", "OR"],
      createdAt: "2026-08-03 11:30",
    },
    {
      _id: "demo_3",
      levelCode: "#CS-9011",
      title: "Overclocked NOR Gate",
      creator: { name: "ByteMaster", email: "byte@circuitscramble.com", avatar: "https://api.dicebear.com/7.x/bottts/svg?seed=Byte" },
      difficulty: "Expert",
      nodesCount: 14,
      edgesCount: 18,
      bestMoves: 12,
      gateTypes: ["NOR", "XOR", "AND", "NOT"],
      createdAt: "2026-08-02 19:40",
    },
  ];

  const columns = [
    { name: "CODE", align: "left" },
    { name: "TITLE", align: "left" },
    { name: "CREATOR", align: "left" },
    { name: "DIFFICULTY", align: "center" },
    { name: "NODES", align: "center" },
    { name: "SUBMITTED AT", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rows = pendingList.map((item) => ({
    CODE: (
      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
        {item.levelCode || "#CS-0000"}
      </VuiTypography>
    ),
    TITLE: (
      <VuiTypography variant="button" color="white" fontWeight="bold">
        {item.title || "Custom Circuit"}
      </VuiTypography>
    ),
    CREATOR: (
      <VuiBox display="flex" alignItems="center" gap={1.5}>
        <Avatar src={item.creator?.avatar || item.creator?.picture} sx={{ width: 28, height: 28 }} />
        <VuiTypography variant="caption" color="text" fontWeight="medium">
          {item.creator?.name || item.creator || "Anonymous"}
        </VuiTypography>
      </VuiBox>
    ),
    DIFFICULTY: (
      <Chip
        label={item.difficulty || "Medium"}
        color={item.difficulty === "Hard" || item.difficulty === "Expert" ? "error" : "warning"}
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    NODES: (
      <VuiTypography variant="caption" color="white" fontWeight="bold">
        {item.nodesCount || item.nodes?.length || 7} Nodes
      </VuiTypography>
    ),
    "SUBMITTED AT": (
      <VuiTypography variant="caption" color="text">
        {item.createdAt}
      </VuiTypography>
    ),
    ACTIONS: (
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => handleOpenPreview(item)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2.5,
          boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
          "&:hover": {
            backgroundColor: "#0891b2 !important",
            boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
          },
        }}
      >
        Preview
      </Button>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <VuiTypography variant="h5" color="white" fontWeight="bold">
              Pending Community Level Approvals
            </VuiTypography>
          </VuiBox>
          <VuiBox p={2}>
            <Table columns={columns} rows={rows} />
          </VuiBox>
        </Card>
      </VuiBox>

      {/* 🎮 Interactive ReactFlow Circuit Canvas Player */}
      {playModalOpen && (
        <RunAndTest
          data={
            selectedLevel?.nodes && selectedLevel?.edges
              ? { levelData: { nodes: selectedLevel.nodes, edges: selectedLevel.edges }, outputId: selectedLevel.completeNodeId || "4" }
              : {
                  levelData: {
                    nodes: [
                      { id: "1", type: "input", data: { label: "Switch 1" }, position: { x: 200, y: 450 } },
                      { id: "2", type: "input", data: { label: "Switch 2" }, position: { x: 400, y: 450 } },
                      { id: "3", type: "gate", data: { label: "AND", inputs: ["1", "2"], outputs: ["4"] }, position: { x: 300, y: 250 } },
                      { id: "4", type: "output", data: { label: "LEVEL COMPLETE", inputs: ["3"] }, position: { x: 300, y: 80 } },
                    ],
                    edges: [
                      { id: "e1-3", source: "1", sourceHandle: "output", target: "3", targetHandle: "input-1", animated: true },
                      { id: "e2-3", source: "2", sourceHandle: "output", target: "3", targetHandle: "input-2", animated: true },
                      { id: "e3-4", source: "3", sourceHandle: "output", target: "4", targetHandle: "input-3", animated: true },
                    ],
                  },
                  outputId: "4",
                }
          }
          onClose={() => setPlayModalOpen(false)}
          onApprove={() => handleApprove(selectedLevel._id)}
          onReject={() => handleOpenRejectModal(selectedLevel)}
        />
      )}

      {/* Level Circuit Tech Preview Modal */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Chip label={selectedLevel?.levelCode} color="primary" sx={{ fontWeight: "bold" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                {selectedLevel?.title}
              </VuiTypography>
            </VuiBox>
            <Chip label={selectedLevel?.difficulty} color="warning" sx={{ fontWeight: "bold" }} />
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {/* Creator Profile Panel */}
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            mb={3}
            sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}
          >
            <VuiBox display="flex" alignItems="center" gap={2}>
              <Avatar src={selectedLevel?.creator?.avatar || selectedLevel?.creator?.picture} />
              <VuiBox display="flex" flexDirection="column">
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  {selectedLevel?.creator?.name || selectedLevel?.creator}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {selectedLevel?.creator?.email}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
            <VuiTypography variant="caption" color="text">
              Submitted: {selectedLevel?.createdAt}
            </VuiTypography>
          </VuiBox>

          {/* Circuit Tech Breakdown Grid */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Memory sx={{ color: "#06b6d4", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">TOTAL NODES</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  {selectedLevel?.nodesCount || 7} Nodes
                </VuiTypography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Extension sx={{ color: "#38bdf8", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text" mb={0.5}>GATES INCLUDED</VuiTypography>
                <VuiBox display="flex" flexWrap="wrap" justifyContent="center" gap={0.8}>
                  {(selectedLevel?.gateTypes || ["XOR", "AND", "NOT"]).map((gate, i) => (
                    <Chip
                      key={i}
                      label={gate}
                      size="small"
                      sx={{
                        backgroundColor: "#06b6d4 !important",
                        color: "#ffffff !important",
                        fontWeight: "bold",
                        fontSize: "11px",
                        px: 0.5,
                      }}
                    />
                  ))}
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, textAlign: "center", minHeight: "130px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Speed sx={{ color: "#f59e0b", mb: 0.5, fontSize: 24 }} />
                <VuiTypography variant="caption" color="text">BEST MOVES TARGET</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  {selectedLevel?.bestMoves || 4} Moves
                </VuiTypography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => {
              setPreviewModalOpen(false);
              handleOpenPlayModal(selectedLevel);
            }}
            variant="contained"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#10b981 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
            }}
          >
            🎮 Test & Play Circuit Diagram
          </Button>
          <Button onClick={() => setPreviewModalOpen(false)} sx={{ color: "#94a3b8" }}>
            Close Preview
          </Button>
          <Button
            onClick={() => handleOpenRejectModal(selectedLevel)}
            variant="contained"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#f43f5e !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
            }}
          >
            Reject Level
          </Button>
          <Button
            onClick={() => handleApprove(selectedLevel._id)}
            variant="contained"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#06b6d4 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              boxShadow: "0 0 14px rgba(6, 182, 212, 0.4)",
            }}
          >
            Approve & Publish Level
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mandatory Rejection Reason Dialog */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #f43f5e",
          },
        }}
      >
        <DialogTitle sx={{ color: "#f43f5e", fontWeight: "bold", borderBottom: "1px solid #1e293b" }}>
          🛑 Reject Level Submission ({selectedLevel?.levelCode})
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <VuiTypography variant="caption" color="text" mb={2} display="block">
            Please state the mandatory rejection reason to notify creator <strong>{selectedLevel?.creator?.name || selectedLevel?.creator}</strong>:
          </VuiTypography>
          
          <Box>
            <label style={{ color: "#fb7185", fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "6px" }}>
              Rejection Reason (Required)
            </label>
            <textarea
              rows={3}
              placeholder="e.g. Unsolvable circuit logic deadlock / overlapping logic gates"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              style={{
                width: "100%",
                backgroundColor: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "10px",
                color: "#ffffff",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button onClick={() => setRejectModalOpen(false)} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            disabled={isRejecting || !rejectionReason.trim()}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#f43f5e !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(244, 63, 94, 0.4)",
            }}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default PendingLevels;
